import React, { useState, useEffect } from "react";
import { useAuth } from "@app/shared";
import * as WizardClient from "@app/shared/src/api/WizardControllerClient.gen";
import { useNavigate } from "react-router-dom";
import { Pages } from "../..";
import { Dialog, Icons } from "../../../shared";

interface WizardProgressDialogProps {
    show?: boolean;
    onClose?: () => void;
    autoCheck?: boolean;
}

export const WizardProgressDialog: React.FC<WizardProgressDialogProps> = ({ show: externalShow, onClose, autoCheck = true }) => {
    const { currentOrganizationId } = useAuth();
    const navigate = useNavigate();
    const [isVisible, setIsVisible] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isResetting, setIsResetting] = useState(false);

    useEffect(() => {
        const checkWizardProgress = async () => {
            if (!currentOrganizationId || !autoCheck) return;

            try {
                const data = await WizardClient.getCurrent(currentOrganizationId);
                if (data && data.currentStep) {
                    setIsVisible(true);
                }
            } catch (error) {
                console.error("Error checking wizard progress:", error);
            } finally {
                setIsLoading(false);
            }
        };

        checkWizardProgress();
    }, [currentOrganizationId, autoCheck]);

    const shouldShow = externalShow !== undefined ? externalShow : isVisible;

    const handleContinueWizard = () => {
        navigate(Pages.WizardSetupDevice.getUrl());
        handleClose();
    };

    const handleSkipWizard = async () => {
        if (!currentOrganizationId) return;

        try {
            setIsResetting(true);
            await WizardClient.reset(currentOrganizationId);
            console.log("Wizard reset successfully");
            handleClose();
        } catch (error) {
            console.error("Error resetting wizard:", error);
            throw error;
        } finally {
            setIsResetting(false);
        }
    };

    const handleClose = () => {
        setIsVisible(false);
        if (onClose) {
            onClose();
        }
    };

    if (isLoading) {
        return null;
    }

    return (
        <Dialog show={shouldShow} title="Device Setup In Progress" onCancelClick={handleClose} showOkButton={false} closeOnOutsideClick={false} className="max-w-md">
            <div className="space-y-6">
                <div className="flex items-start gap-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-brand-primary to-brand-primary-darkened rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg">
                        <Icons.Settings className="w-8 h-8 text-white" />
                    </div>
                    <div className="flex-1">
                        <h3 className="text-xl font-bold text-neutral-900 mb-2">Resume Your Device Setup?</h3>
                        <p className="text-neutral-600 leading-relaxed">Looks like you were in the middle of setting up a device. Would you like to continue where you left off?</p>
                    </div>
                </div>

                <div className="space-y-3">
                    <button onClick={handleContinueWizard} className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-brand-primary hover:bg-brand-primary-darkened text-white font-semibold rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl">
                        <Icons.Play className="w-4 h-4" />
                        Continue Device Setup
                    </button>

                    <button onClick={handleSkipWizard} disabled={isResetting} className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-white border border-warmGray-300 hover:border-warmGray-400 text-neutral-700 font-semibold rounded-xl transition-all duration-200 hover:bg-warmGray-50 disabled:opacity-50 disabled:cursor-not-allowed">
                        {isResetting ? (
                            <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-neutral-700"></div>
                                Resetting...
                            </>
                        ) : (
                            <>
                                <Icons.PlaySkipForward className="w-4 h-4" />
                                Skip Setup
                            </>
                        )}
                    </button>
                </div>
            </div>
        </Dialog>
    );
};
